import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Globe, Languages, Download, Settings, Search, SortAsc, SortDesc, Grid, List, Trash2, FileText, Target, FileText as FileTextIcon } from 'lucide-react';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  isRTL: boolean;
  isSupported: boolean;
  isDownloaded: boolean;
  downloadSize: number;
  lastUpdated: Date;
  version: string;
  quality: 'low' | 'medium' | 'high';
  coverage: number; // percentage of content translated
}

interface Translation {
  id: string;
  language: string;
  originalText: string;
  translatedText: string;
  confidence: number;
  isVerified: boolean;
  isUserGenerated: boolean;
  createdAt: Date;
  updatedAt: Date;
  translator: string;
  notes: string;
}

interface MultiLanguageSupportProps {
  onLanguageChange?: (language: string) => void;
  onTranslationRequest?: (text: string, targetLanguage: string) => void;
  onTranslationUpdate?: (translation: Translation) => void;
  onLanguageDownload?: (language: string) => void;
  onLanguageDelete?: (language: string) => void;
}

const MultiLanguageSupport: React.FC<MultiLanguageSupportProps> = ({
  onLanguageChange,
  onTranslationRequest,
  onTranslationUpdate,
  onLanguageDownload,
  onLanguageDelete
}) => {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [selectedLanguages, setSelectedLanguages] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'coverage' | 'lastUpdated' | 'downloadSize'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [autoTranslate, setAutoTranslate] = useState(false);
  const [translationQuality, setTranslationQuality] = useState<'low' | 'medium' | 'high'>('medium');
  const [showTranslationModal, setShowTranslationModal] = useState(false);
  const [translationText, setTranslationText] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('');

  useEffect(() => {
    loadLanguages();
    loadTranslations();
  }, []);

  const loadLanguages = async () => {
    try {
      setIsLoading(true);
      // In a real app, this would load from an API
      const sampleLanguages: Language[] = [
        {
          code: 'en',
          name: 'English',
          nativeName: 'English',
          flag: '🇺🇸',
          isRTL: false,
          isSupported: true,
          isDownloaded: true,
          downloadSize: 0,
          lastUpdated: new Date('2024-01-01'),
          version: '1.0.0',
          quality: 'high',
          coverage: 100
        },
        {
          code: 'es',
          name: 'Spanish',
          nativeName: 'Español',
          flag: '🇪🇸',
          isRTL: false,
          isSupported: true,
          isDownloaded: true,
          downloadSize: 2048576, // 2MB
          lastUpdated: new Date('2024-01-15'),
          version: '1.0.0',
          quality: 'high',
          coverage: 95
        },
        {
          code: 'fr',
          name: 'French',
          nativeName: 'Français',
          flag: '🇫🇷',
          isRTL: false,
          isSupported: true,
          isDownloaded: true,
          downloadSize: 1536000, // 1.5MB
          lastUpdated: new Date('2024-01-10'),
          version: '1.0.0',
          quality: 'high',
          coverage: 90
        },
        {
          code: 'de',
          name: 'German',
          nativeName: 'Deutsch',
          flag: '🇩🇪',
          isRTL: false,
          isSupported: true,
          isDownloaded: false,
          downloadSize: 2560000, // 2.5MB
          lastUpdated: new Date('2024-01-05'),
          version: '1.0.0',
          quality: 'medium',
          coverage: 85
        },
        {
          code: 'it',
          name: 'Italian',
          nativeName: 'Italiano',
          flag: '🇮🇹',
          isRTL: false,
          isSupported: true,
          isDownloaded: false,
          downloadSize: 1800000, // 1.8MB
          lastUpdated: new Date('2024-01-08'),
          version: '1.0.0',
          quality: 'medium',
          coverage: 80
        },
        {
          code: 'pt',
          name: 'Portuguese',
          nativeName: 'Português',
          flag: '🇵🇹',
          isRTL: false,
          isSupported: true,
          isDownloaded: false,
          downloadSize: 2200000, // 2.2MB
          lastUpdated: new Date('2024-01-12'),
          version: '1.0.0',
          quality: 'medium',
          coverage: 75
        },
        {
          code: 'ru',
          name: 'Russian',
          nativeName: 'Русский',
          flag: '🇷🇺',
          isRTL: false,
          isSupported: true,
          isDownloaded: false,
          downloadSize: 3000000, // 3MB
          lastUpdated: new Date('2024-01-03'),
          version: '1.0.0',
          quality: 'medium',
          coverage: 70
        },
        {
          code: 'ja',
          name: 'Japanese',
          nativeName: '日本語',
          flag: '🇯🇵',
          isRTL: false,
          isSupported: true,
          isDownloaded: false,
          downloadSize: 4000000, // 4MB
          lastUpdated: new Date('2024-01-07'),
          version: '1.0.0',
          quality: 'low',
          coverage: 60
        },
        {
          code: 'ko',
          name: 'Korean',
          nativeName: '한국어',
          flag: '🇰🇷',
          isRTL: false,
          isSupported: true,
          isDownloaded: false,
          downloadSize: 3500000, // 3.5MB
          lastUpdated: new Date('2024-01-09'),
          version: '1.0.0',
          quality: 'low',
          coverage: 55
        },
        {
          code: 'zh',
          name: 'Chinese',
          nativeName: '中文',
          flag: '🇨🇳',
          isRTL: false,
          isSupported: true,
          isDownloaded: false,
          downloadSize: 4500000, // 4.5MB
          lastUpdated: new Date('2024-01-11'),
          version: '1.0.0',
          quality: 'low',
          coverage: 50
        },
        {
          code: 'ar',
          name: 'Arabic',
          nativeName: 'العربية',
          flag: '🇸🇦',
          isRTL: true,
          isSupported: true,
          isDownloaded: false,
          downloadSize: 2800000, // 2.8MB
          lastUpdated: new Date('2024-01-06'),
          version: '1.0.0',
          quality: 'medium',
          coverage: 65
        },
        {
          code: 'hi',
          name: 'Hindi',
          nativeName: 'हिन्दी',
          flag: '🇮🇳',
          isRTL: false,
          isSupported: true,
          isDownloaded: false,
          downloadSize: 3200000, // 3.2MB
          lastUpdated: new Date('2024-01-04'),
          version: '1.0.0',
          quality: 'low',
          coverage: 45
        }
      ];
      setLanguages(sampleLanguages);
    } catch (err) {
      setError('Failed to load languages');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTranslations = async () => {
    try {
      // In a real app, this would load from an API
      const sampleTranslations: Translation[] = [
        {
          id: '1',
          language: 'es',
          originalText: 'Welcome to PostSub.io',
          translatedText: 'Bienvenido a PostSub.io',
          confidence: 0.95,
          isVerified: true,
          isUserGenerated: false,
          createdAt: new Date('2024-01-01'),
          updatedAt: new Date('2024-01-01'),
          translator: 'system',
          notes: 'Official translation'
        },
        {
          id: '2',
          language: 'fr',
          originalText: 'Create your newsletter',
          translatedText: 'Créez votre newsletter',
          confidence: 0.92,
          isVerified: true,
          isUserGenerated: false,
          createdAt: new Date('2024-01-02'),
          updatedAt: new Date('2024-01-02'),
          translator: 'system',
          notes: 'Official translation'
        },
        {
          id: '3',
          language: 'de',
          originalText: 'Publish and share',
          translatedText: 'Veröffentlichen und teilen',
          confidence: 0.88,
          isVerified: false,
          isUserGenerated: true,
          createdAt: new Date('2024-01-03'),
          updatedAt: new Date('2024-01-03'),
          translator: 'user123',
          notes: 'Community translation'
        }
      ];
      setTranslations(sampleTranslations);
    } catch (err) {
      setError('Failed to load translations');
    }
  };

  const handleLanguageChange = (languageCode: string) => {
    setCurrentLanguage(languageCode);
    onLanguageChange?.(languageCode);
  };

  const handleLanguageDownload = async (languageCode: string) => {
    try {
      setIsLoading(true);
      // Simulate download
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setLanguages(prev => 
        prev.map(lang => 
          lang.code === languageCode 
            ? { ...lang, isDownloaded: true }
            : lang
        )
      );
      
      onLanguageDownload?.(languageCode);
    } catch (err) {
      setError('Failed to download language pack');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageDelete = (languageCode: string) => {
    setLanguages(prev => 
      prev.map(lang => 
        lang.code === languageCode 
          ? { ...lang, isDownloaded: false }
          : lang
      )
    );
    onLanguageDelete?.(languageCode);
  };

  const handleTranslationRequest = async (text: string, targetLanguage: string) => {
    try {
      setIsLoading(true);
      // Simulate translation API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const translation: Translation = {
        id: Date.now().toString(),
        language: targetLanguage,
        originalText: text,
        translatedText: `[Translated to ${targetLanguage}] ${text}`,
        confidence: 0.85,
        isVerified: false,
        isUserGenerated: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        translator: 'user',
        notes: 'Auto-translated'
      };
      
      setTranslations(prev => [...prev, translation]);
      onTranslationRequest?.(text, targetLanguage);
    } catch (err) {
      setError('Failed to translate text');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectLanguage = (languageCode: string) => {
    setSelectedLanguages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(languageCode)) {
        newSet.delete(languageCode);
      } else {
        newSet.add(languageCode);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedLanguages.size === filteredLanguages.length) {
      setSelectedLanguages(new Set());
    } else {
      setSelectedLanguages(new Set(filteredLanguages.map(lang => lang.code)));
    }
  };

  const handleBulkDownload = async () => {
    const languageCodes = Array.from(selectedLanguages);
    for (const languageCode of languageCodes) {
      await handleLanguageDownload(languageCode);
    }
    setSelectedLanguages(new Set());
  };

  const handleBulkDelete = () => {
    const languageCodes = Array.from(selectedLanguages);
    languageCodes.forEach(languageCode => {
      handleLanguageDelete(languageCode);
    });
    setSelectedLanguages(new Set());
  };

  const formatFileSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getQualityColor = (quality: string) => {
    switch (quality) {
      case 'high': return 'text-green-600 dark:text-green-400';
      case 'medium': return 'text-yellow-600 dark:text-yellow-400';
      case 'low': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getQualityBgColor = (quality: string) => {
    switch (quality) {
      case 'high': return 'bg-green-100 dark:bg-green-900';
      case 'medium': return 'bg-yellow-100 dark:bg-yellow-900';
      case 'low': return 'bg-red-100 dark:bg-red-900';
      default: return 'bg-gray-100 dark:bg-gray-800';
    }
  };

  const filteredLanguages = languages
    .filter(lang => {
      const matchesStatus = filterStatus === 'all' || 
        (filterStatus === 'downloaded' && lang.isDownloaded) ||
        (filterStatus === 'available' && !lang.isDownloaded);
      const matchesSearch = lang.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           lang.nativeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           lang.code.toLowerCase().includes(searchQuery.toLowerCase());
      
      return matchesStatus && matchesSearch;
    })
    .sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'coverage':
          aValue = a.coverage;
          bValue = b.coverage;
          break;
        case 'lastUpdated':
          aValue = a.lastUpdated.getTime();
          bValue = b.lastUpdated.getTime();
          break;
        case 'downloadSize':
          aValue = a.downloadSize;
          bValue = b.downloadSize;
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  if (isLoading && languages.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <div className="text-red-500 mb-4">{error}</div>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Multi-Language Support
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Translate content and switch between languages
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
              <Globe className="h-4 w-4" />
              <span className="text-sm font-medium">
                {languages.find(lang => lang.code === currentLanguage)?.name || 'English'}
              </span>
            </div>
            
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <Settings className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search languages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Languages</option>
              <option value="downloaded">Downloaded</option>
              <option value="available">Available</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="name">Name</option>
              <option value="coverage">Coverage</option>
              <option value="lastUpdated">Last Updated</option>
              <option value="downloadSize">Download Size</option>
            </select>

            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
            </button>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>

            {selectedLanguages.size > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleBulkDownload}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download ({selectedLanguages.size})
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete ({selectedLanguages.size})
                </button>
              </div>
            )}

            <button
              onClick={() => setShowTranslationModal(true)}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center"
            >
              <Languages className="h-4 w-4 mr-2" />
              Translate
            </button>
          </div>
        </div>
      </div>

      {/* Languages Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredLanguages.map((language) => (
            <div
              key={language.code}
              className={`bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow ${
                currentLanguage === language.code ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedLanguages.has(language.code)}
                    onChange={() => handleSelectLanguage(language.code)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-2xl">{language.flag}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getQualityBgColor(language.quality)} ${getQualityColor(language.quality)}`}>
                    {language.quality}
                  </span>
                  {language.isRTL && (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-400">
                      RTL
                    </span>
                  )}
                </div>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                {language.name}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {language.nativeName}
              </p>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                  <span>Coverage</span>
                  <span>{language.coverage}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${language.coverage}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                  <span>Version</span>
                  <span>{language.version}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                  <span>Size</span>
                  <span>{formatFileSize(language.downloadSize)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                  <span>Updated</span>
                  <span>{formatDate(language.lastUpdated)}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {language.isDownloaded ? (
                    <button
                      onClick={() => handleLanguageDelete(language.code)}
                      className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      onClick={() => handleLanguageDownload(language.code)}
                      className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Download
                    </button>
                  )}
                </div>
                
                <button
                  onClick={() => handleLanguageChange(language.code)}
                  className={`px-3 py-1 text-sm rounded ${
                    currentLanguage === language.code
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {currentLanguage === language.code ? 'Active' : 'Select'}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLanguages.map((language) => (
            <div
              key={language.code}
              className={`bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 ${
                currentLanguage === language.code ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900' : ''
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <input
                    type="checkbox"
                    checked={selectedLanguages.has(language.code)}
                    onChange={() => handleSelectLanguage(language.code)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-2xl">{language.flag}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {language.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {language.nativeName}
                    </p>
                  </div>
                </div>

                <div className="text-right">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getQualityBgColor(language.quality)} ${getQualityColor(language.quality)}`}>
                      {language.quality}
                    </span>
                    {language.isRTL && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-400">
                        RTL
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {language.coverage}% coverage • {formatFileSize(language.downloadSize)}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-500">
                    Updated: {formatDate(language.lastUpdated)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredLanguages.length === 0 && (
        <div className="text-center py-12">
          <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No languages found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Try adjusting your search or filter criteria
          </p>
        </div>
      )}

      {/* Translation Modal */}
      {showTranslationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Translate Text
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Text to translate
                </label>
                <textarea
                  value={translationText}
                  onChange={(e) => setTranslationText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Enter text to translate..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Target language
                </label>
                <select
                  value={targetLanguage}
                  onChange={(e) => setTargetLanguage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select language</option>
                  {languages.filter(lang => lang.isDownloaded).map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => setShowTranslationModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (translationText && targetLanguage) {
                    handleTranslationRequest(translationText, targetLanguage);
                    setShowTranslationModal(false);
                    setTranslationText('');
                    setTargetLanguage('');
                  }
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Translate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Language Settings
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Auto-translate new content
                </label>
                <input
                  type="checkbox"
                  checked={autoTranslate}
                  onChange={(e) => setAutoTranslate(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Translation Quality
                </label>
                <select
                  value={translationQuality}
                  onChange={(e) => setTranslationQuality(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="low">Low (faster)</option>
                  <option value="medium">Medium (balanced)</option>
                  <option value="high">High (best quality)</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiLanguageSupport;
